import { Button } from '@assembly-js/design-system';
import React, { useContext, useEffect, useMemo } from 'react';

import { AlertCircleIcon } from 'src/components/icons';
import { LoadingWrapper } from 'src/components/loading/LoadingWrapper';
import { DATE_FORMAT, HOME_PAGE, REGISTER_PAGE } from 'src/constants';
import { PortalConfigContext, RouteContext } from 'src/context';
import { usePaymentOptions } from 'src/features/payments/hooks/usePaymentOptions';
import { usePaymentProcessingFee } from 'src/features/payments/hooks/usePaymentProcessingFee';
import history from 'src/history';
import { useAppSelector } from 'src/hooks/useStore';
import { useWindowSize } from 'src/hooks/useWindowResize';
import StandardPage from 'src/legacy/components/UI/StandardPage';
import { Checkout } from 'src/routes/payments/Checkout/Checkout';
import { LineItemsSchema } from 'src/routes/payments/invoices/types';
import { PaymentLinkCheckoutDetail } from 'src/routes/payments/payment-links/components/PaymentLinkCheckoutDetail';
import { LineItemType } from 'src/services/api/invoicesApi';
import {
  LineItem,
  PaymentLinkStatus,
  UpdatePaymentLinkInput,
  useGetPaymentLinkQuery,
} from 'src/services/api/paymentLinksApi';
import { StripeElementsLayout, StripePaymentMethod } from 'src/types/payment';
import { DateUtils } from 'src/utils';
import InvoiceUtils from 'src/utils/InvoiceUtils';

export interface CheckoutPaymentLink extends Partial<UpdatePaymentLinkInput> {
  id: string;
  currency: string;
  amountDue: number;
  tax: number;
  ignoreFees: boolean;
  fees: number;
  lineItems: LineItem[];
  interval?: string;
  layout: StripeElementsLayout;
  isLoggedInUser?: boolean;
  accountId?: string;
}

const taxPercentage = 0;

export const PaymentLinkPay = () => {
  const [selectedSource, setSelectedSource] = React.useState<
    StripePaymentMethod | string | null
  >(null);
  const { pathname } = useContext(RouteContext);
  const pathParams = pathname.split('/'); // structured like /id/title
  const paymentLinkId = pathParams[pathParams.length - 2] ?? '';
  const { instance: isLoggedInUser } = useAppSelector((state) => state.user);

  const {
    data: paymentLink,
    error: loadPaymentLinkErr,
    isLoading: paymentLinkLoading,
  } = useGetPaymentLinkQuery(paymentLinkId);

  const portalConfig = React.useContext(PortalConfigContext);

  const allowSignup = !portalConfig.features.signupDisabled;
  // This useEffect exists to manage redirects when public payment links are disabled
  // In that case, when we fetch the payment link, it will fail with an authorization error
  // If the feature flag is off and we get that error, we want to redirect to login
  useEffect(() => {
    if (loadPaymentLinkErr) {
      window.location.href = `/login?step=signIn&next=${window.location.pathname}`;
    }
  }, [loadPaymentLinkErr]);

  const lineItems = paymentLink?.lineItems || [];

  const { differentialRatio } = useWindowSize(1024);

  // For payment links, we always have product line items
  // But `LineItemSchema` is a widely used type in the codebase
  // And it represent both one off and product line items
  // Hence the type checking
  const schemaLineItems: LineItemsSchema = lineItems.map((lineItem) => {
    if (lineItem.productId) {
      return {
        productId: lineItem.productId,
        type: LineItemType.Product,
        priceId: lineItem.priceId!,
        id: lineItem.id,
        quantity: lineItem.quantity.toString(),
        amount: (lineItem.amount || 0).toString(),
        description: lineItem.description || '',
        isEditing: false,
        isValid: true,
        shouldAddPrice: false,
      };
    } else {
      return {
        type: LineItemType.OneTime,
        id: lineItem.id,
        quantity: lineItem.quantity.toString(),
        amount: (lineItem.amount || 0).toString(),
        description: lineItem.description || '',
        isEditing: false,
        isValid: true,
        shouldAddProduct: false,
      };
    }
  });
  const totalAmount = InvoiceUtils.GetTotalAmount(schemaLineItems, '0');

  const { fee, ignoreFees, label } = usePaymentProcessingFee({
    amountDue: totalAmount,
    source: selectedSource,
    preferences: paymentLink?.paymentPreferences || {
      absorbTransactionFee: [],
      paymentMethodTypes: [],
    },
  });

  const layout =
    differentialRatio > 1
      ? StripeElementsLayout.Tabs
      : StripeElementsLayout.Accordion;

  const paymentOptions = usePaymentOptions(
    paymentLink?.paymentPreferences,
    paymentLink?.currency,
  );

  const paymentLinkInfo = React.useMemo(() => {
    if (!paymentLink) return;
    const { coverUrl } = paymentLink;
    const isTargetSubscription =
      paymentLink.interval && paymentLink.interval !== '';

    return {
      id: paymentLink.id,
      name: paymentLink.name,
      currency: paymentLink.currency,
      description: paymentLink.description,
      fees: fee,
      tax: taxPercentage,
      amountDue: totalAmount,
      ignoreFees,
      dateOfIssue: DateUtils.formatDate(
        paymentLink?.createdAt ?? '',
        DATE_FORMAT,
      ),
      coverUrl,
      lineItems: paymentLink.lineItems,
      feeLabel: label,
      // Currently we support only one product, so we can take the interval from the first line item
      interval: paymentLink.interval,
      accountId: paymentLink.accountId,
      isTargetSubscription,
      customerSessionTargetOverride: isTargetSubscription
        ? 'purchase:subscription'
        : 'purchase:invoice',
      layout,
    };
  }, [paymentLink, totalAmount, fee, ignoreFees, label, layout]);

  const isPaymentLinkActive = paymentLink?.status === PaymentLinkStatus.Active;

  // If public payment links are disabled, we want to show a loading spinner until a redirect is made
  // If they are on, a failure to fetch means the link is no longer active, so we show an error
  const showLoadingSpinner = paymentLinkLoading || !!loadPaymentLinkErr;

  const isSubscription =
    paymentLinkInfo?.interval && paymentLinkInfo.interval !== '';

  const handleBack = () => {
    if (isLoggedInUser) {
      history.push(HOME_PAGE.path);
    } else {
      history.push(REGISTER_PAGE);
    }
  };

  const showBackButton = useMemo(() => {
    if (isLoggedInUser || allowSignup) {
      return true;
    } else {
      return false;
    }
  }, [isLoggedInUser, allowSignup]);

  return (
    <LoadingWrapper
      isLoading={showLoadingSpinner}
      hideContentWhileLoading
      wrapperProps={{ height: '100%' }}
    >
      <div className="h-full box-border lg:@container grid-container sm:overflow-hidden sm:overflow-y-auto overflow-auto block sm:flex pb-[60px] sm:pb-0">
        {isPaymentLinkActive ? (
          paymentLinkInfo &&
          !loadPaymentLinkErr && (
            <Checkout<CheckoutPaymentLink>
              recipientId={''}
              isActualCheckout={true}
              target={paymentLinkInfo}
              selectedSource={selectedSource}
              setSelectedSource={setSelectedSource}
              paymentOptions={paymentOptions}
              isLoggedInUser={!!isLoggedInUser}
              ContextSectionComponent={PaymentLinkCheckoutDetail}
              paymentMode="paymentLink"
              afterCheckoutConfig={{
                headerText: isSubscription
                  ? 'Thanks for subscribing'
                  : 'Thanks for your payment',
                descriptionText: `A payment to ${portalConfig.name} will appear in your invoices.`,
                magicLinkSentDescriptionText: `Your ${
                  isSubscription ? 'subscription' : 'payment'
                } includes access to ${
                  portalConfig.name
                }'s client portal. You can access it by signing in with a magic link.`,
                linkText: 'Continue to the portal',
              }}
            />
          )
        ) : (
          <StandardPage
            topIcon={<AlertCircleIcon className="w-[25px] h-[25px] mt-[2px]" />}
            titleForClient="The link is no longer active."
            descForClient={
              showBackButton
                ? 'Explore the Portal to find what you’re looking for.'
                : ''
            }
            actionButtonForClient={
              showBackButton ? (
                <Button onClick={handleBack} label="Back" />
              ) : null
            }
          />
        )}
      </div>
    </LoadingWrapper>
  );
};
